import type { ExperimentConfig } from '@/types';

const experiment11Config: ExperimentConfig = {
  id: 'exp11',
  number: 11,
  name: 'Aspirin Determination',
  description: 'Determine the percentage of ASA (acetylsalicylic acid) in aspirin tablets using potentiometric titration with standardized NaOH. Monitor pH changes and determine the equivalence point by finding the inflection point of the titration curve.',
  estimatedTime: '90 minutes',
  learningObjectives: [
    'Calibrate a pH meter: pH 7 (standardize), then pH 4 (second buffer)',
    'Prepare electrodes — ensure filled with saturated KCl solution',
    'Standardize 0.1 M NaOH by titrating 25.00 mL of 0.10 M HCl',
    'Titration increments: 5.0 mL for first 20.0 mL, then 1.0 mL until within 2 mL of endpoint, then 0.5 mL, then 0.1 mL near endpoint',
    'Weigh 4 aspirin tablets, crush and dissolve in ~40 mL 95% ethanol in 100 mL volumetric flask, dilute to mark',
    'Pipet 50 mL of aspirin solution into 250 mL beaker, add 100 mL of water',
    'Construct and interpret S-shaped titration curves (pH vs. volume)',
    'Determine equivalence point by finding the inflection point of the curve',
    'Aspirin (acetylsalicylic acid / ASA) is a weak acid, MW = 180.16 g/mol, 1:1 stoichiometry with NaOH',
    'Calculate the percentage of ASA in a typical aspirin tablet',
    'Ka = [A⁻][H₃O⁺] / [HA] — smaller Ka means weaker acid',
  ],
  safetyNotes: [
    'Handle 0.1 M NaOH solutions with care - caustic',
    'The glass electrode is extremely fragile and must be treated with due care',
    'Rinse electrode with deionised water between ALL measurements',
    'BE CAREFUL — pH probe is sensitive and will be damaged if stir bar strikes it with sufficient force',
    'Ensure electrodes are properly filled with saturated KCl solution',
  ],
  requiredEquipment: [
    'pH meter with combination electrode (filled with saturated KCl)',
    'Burette',
    'Burette stand and clamp',
    '250 mL beaker',
    '100 mL volumetric flask',
    'Magnetic stirrer and 3/4 inch stir bar',
    'Analytical balance',
    'Small beakers for buffers',
    'Buffer solutions (pH 4, pH 7)',
    'Wash bottle (deionised water)',
    '0.1 M NaOH solution',
    '0.10 M HCl standard solution',
    'Aspirin tablets (4)',
    '95% ethanol (CH₃CH₂OH)',
    'Graph paper or Excel',
  ],
  parts: [
    {
      id: 0,
      name: 'summary',
      title: 'Summary',
      steps: [],
      equipment: [],
      initialState: {},
    },
    {
      id: 1,
      name: 'calibration',
      title: 'Part I: pH Meter Calibration',
      equipment: [
        'pH meter with combination electrode',
        'Buffer solutions (pH 4, pH 7)',
        'Small beakers',
        'Wash bottle (deionised water)',
      ],
      initialState: {
        pHMeter: {
          isOn: true,
          isCalibrated: false,
          currentReading: null,
          temperature: 25.0,
          stability: false
        },
        calibration: {
          pH7: { buffer: 'pH 7', reading: null, set: false },
          pH4: { buffer: 'pH 4', reading: null, set: false },
          pH10: { buffer: 'pH 10', reading: null, verified: false }
        }
      },
      steps: [
        {
          id: 1,
          title: 'Prepare Electrodes',
          shortDescription: 'Ensure electrodes are properly filled with saturated KCl solution. Ask instructor to add more electrolyte if necessary.',
          handler: 'handlePrepareElectrodes',
          infoContent: {
            title: 'Electrode Preparation',
            content: 'Make sure that the electrodes are properly filled with **saturated KCl solution**. If necessary, ask the instructor to add more electrolyte. The glass electrode is extremely fragile and must be treated with due care.',
            whatHappens: 'The combination electrode is inspected and filled with saturated KCl solution to ensure proper electrical contact for accurate pH measurements.',
            observe: [
              'Check the KCl filling level in the electrode — it should be near the fill hole',
              'Ask the instructor if more electrolyte is needed',
              'Handle the glass electrode with extreme care — it is very fragile',
            ],
          }
        },
        {
          id: 2,
          title: 'Rinse Electrode',
          shortDescription: 'Rinse the electrodes with deionised water',
          handler: 'handleRinseElectrode'
        },
        {
          id: 3,
          title: 'Standardize at pH 7',
          shortDescription: 'CAREFULLY immerse electrode in pH 7 buffer. Turn standardize knob until reading matches pH 7.',
          handler: 'handleCalibratePH7',
          infoContent: {
            title: 'Standardization — pH 7',
            content: 'Rinse the electrodes with deionised water. **CAREFULLY** immerse the electrode in a buffer solution (pH 7) in a small beaker. The glass electrode is extremely fragile. Turn the standardize knob until the displayed reading matches the pH of the standard buffer.',
            whatHappens: 'The pH meter is standardized at pH 7 by adjusting the zero point so that the reading matches the known buffer value.',
            observe: [
              'Rinse the electrode with deionised water before immersing in the buffer',
              'Carefully immerse the electrode in the pH 7 buffer solution',
              'Adjust the standardize knob until the display reads exactly 7.00',
            ],
          }
        },
        {
          id: 4,
          title: 'Calibrate with pH 4',
          shortDescription: 'Repeat with pH 4 buffer. Rinse with deionised water between buffers.',
          handler: 'handleCalibratePH4',
          validation: {
            requires: ['calibration.pH7.set']
          },
          infoContent: {
            title: 'Second Buffer — pH 4',
            content: 'Repeat with a second buffer (pH 4). Lift the electrode out of the buffer and rinse with deionised water. The pH meter is now ready for use.',
            whatHappens: 'A second calibration point at pH 4 sets the electrode slope, completing the two-point calibration required for accurate measurements across the acidic range.',
            observe: [
              'Rinse the electrode with deionised water after removing from pH 7 buffer',
              'Immerse in pH 4 buffer and verify the reading matches',
              'The two-point calibration ensures accuracy across the pH range used in titration',
            ],
          }
        }
      ]
    },
    {
      id: 2,
      name: 'samplePreparation',
      title: 'Part II: Standardization of NaOH and Sample Preparation',
      equipment: [
        'Analytical balance',
        '250 mL beaker',
        '100 mL volumetric flask',
        'Magnetic stirrer',
        '3/4 inch stir bar',
        'Burette',
        '0.10 M HCl standard',
        '0.1 M NaOH',
        '95% ethanol',
        'Aspirin tablets (4)',
      ],
      initialState: {
        balance: {
          reading: 0,
          isTared: false,
          isStable: true
        },
        standardization: {
          HClVolume: 25.00,
          HClMolarity: 0.10,
          NaOHEndpointVolume: null,
          NaOHMolarity: null
        },
        sample: {
          fourTabletMass: null,
          averageTabletMass: null,
          crushed: false,
          dissolved: false
        },
        flask: {
          solutionVolume: 0,
          stirring: false,
          electrodeInserted: false
        }
      },
      steps: [
        {
          id: 1,
          title: 'Standardize NaOH',
          shortDescription: 'Pipet 25.00 mL of 0.10 M HCl into 250 mL beaker with 3/4 inch stir bar. Titrate with NaOH.',
          handler: 'handleStandardizeNaOH',
          infoContent: {
            title: 'Standardization of 0.1 M NaOH',
            content: `Pipet a **25.00 mL** sample of standard **0.10 M HCl** solution into a **250 mL beaker** and add a small **(3/4") magnetic stirring bar**. BE CAREFUL AS THE pH PROBE IS SENSITIVE AND WILL BE DAMAGED IF THE STIR BAR STRIKES IT WITH SUFFICIENT FORCE.

Immerse the electrodes in the solution. Measure the pH. Begin titrating by adding **5.0 mL increments** of NaOH for first **20.0 mL**. Record titrant volume and pH after each addition. Then add **1.0 mL** increments until within 2 mL of end point; then **0.5 mL** increments. As the endpoint is approached, add **0.1 mL** increments. Continue until pH no longer changes (to **pH 12**). Determine the end point and calculate the concentration of NaOH.`,
            whatHappens: 'The NaOH solution is standardized by titrating a known volume of standard HCl, monitoring pH to find the equivalence point and calculate the exact NaOH concentration.',
            observe: [
              'pH rises slowly at first, then shows a sharp jump at the equivalence point (~pH 7)',
              'Use progressively smaller increments (5.0 -> 1.0 -> 0.5 -> 0.1 mL) near the endpoint',
              'The stir bar must not strike the fragile pH electrode',
              'Continue adding NaOH until pH reaches ~12 and no longer changes',
            ],
          }
        },
        {
          id: 2,
          title: 'Weigh 4 Aspirin Tablets',
          shortDescription: 'Weigh 4 aspirin tablets. Record the weight. Determine the average weight/tablet.',
          handler: 'handleWeighTablet',
          validation: {
            requires: ['balance.isTared']
          }
        },
        {
          id: 3,
          title: 'Crush and Dissolve in Ethanol',
          shortDescription: 'Crush 4 tablets, dissolve in about 40 mL 95% ethanol in a 100 mL volumetric flask, dilute to mark with deionised water',
          handler: 'handleCrushTablet',
          infoContent: {
            title: 'Aspirin Dissolution',
            content: 'Crush and dissolve **4 aspirin tablets** in about **40 mL 95% ethanol** (CH₃CH₂OH) in a **100 mL volumetric flask** and dilute to the mark with **deionised water**. Mix thoroughly.',
            whatHappens: 'Crushed aspirin tablets are dissolved in ethanol (which dissolves ASA better than water alone), then diluted to exactly 100 mL to create a solution of known volume.',
            observe: [
              'Crush the tablets thoroughly to a fine powder for faster dissolution',
              'The ASA dissolves in the ethanol; some insoluble binder/filler may remain',
              'Dilute to the 100 mL mark precisely with deionised water and mix well',
            ],
          }
        },
        {
          id: 4,
          title: 'Prepare Titration Solution',
          shortDescription: 'Pipet 50 mL of aspirin solution into 250 mL beaker, add 100 mL of water',
          handler: 'handleTransferToFlask',
          infoContent: {
            title: 'Titration Solution',
            content: 'Pipet **50 mL** of the aspirin solution into a **250 mL beaker** and add **100 mL of water**. Immerse the electrode into the solution and place a stirring bar on the bottom of the beaker. Make sure that the electrode is clear of the stirring bar.',
            whatHappens: 'Half of the aspirin solution (50 mL) is transferred to the titration beaker and diluted with 100 mL of water to provide sufficient volume for electrode immersion and titration.',
            observe: [
              'Pipet exactly 50 mL of the aspirin solution into the 250 mL beaker',
              'Add 100 mL of deionised water to dilute the solution',
              'Position the stir bar so it cannot strike the fragile pH electrode',
            ],
          }
        },
        {
          id: 5,
          title: 'Insert pH Electrode',
          shortDescription: 'Immerse electrode into the solution. Ensure electrode is clear of stir bar.',
          handler: 'handleInsertElectrode',
        }
      ]
    },
    {
      id: 3,
      name: 'titration',
      title: 'Part III: Potentiometric Titration',
      equipment: [
        'Burette (50 mL)',
        'Burette stand and clamp',
        'pH meter with electrode',
        'Magnetic stirrer',
        'Wash bottle'
      ],
      initialState: {
        burette: {
          initialVolume: 0,
          currentVolume: 0,
          NaOH_molarity: 0.1,
          tipOpen: false
        },
        titration: {
          dataPoints: [],
          currentVolume: 0,
          currentPH: null,
          incrementSize: 5.0,
          nearEndpoint: false
        },
        analysis: {
          equivalenceVolume: null,
          firstDerivative: [],
          secondDerivative: []
        }
      },
      steps: [
        {
          id: 1,
          title: 'Fill Burette',
          shortDescription: 'Fill the burette with standardized NaOH solution',
          handler: 'handleFillBurette'
        },
        {
          id: 2,
          title: 'Record Initial pH',
          shortDescription: 'Measure the initial pH of the aspirin solution',
          handler: 'handleRecordInitialPH',
          infoContent: {
            title: 'Aspirin is a Weak Acid',
            content: 'Aspirin (acetylsalicylic acid / ASA) is classified as a weak acid. The initial pH reflects this weak acidity. Record the pH.',
            whatHappens: 'The initial pH of the aspirin solution is measured before adding any NaOH, establishing the starting point for the S-shaped titration curve.',
            observe: [
              'The initial pH should be acidic (typically pH 2-4), reflecting the weak acid nature of ASA',
              'Record the pH precisely before any NaOH is added',
              'This is the first data point (0 mL NaOH) on the titration curve',
            ],
          }
        },
        {
          id: 3,
          title: 'Titrate: 5.0 mL Increments',
          shortDescription: 'Add standard NaOH in 5.0 mL increments for the first 20.0 mL. Record volume and pH after each.',
          handler: 'handleTitrateInitial',
          infoContent: {
            title: 'Data Collection — Initial Region',
            content: 'Add NaOH solution from the burette in **5.0 mL increments** for the first **20.0 mL**. Record the titrant volume and pH after each addition. pH rises slowly followed by a sharp rise — add very slowly during the sharp rise.',
            whatHappens: 'NaOH is added in 5.0 mL increments during the buffer region where pH changes slowly; the weak acid and its conjugate base buffer the solution.',
            observe: [
              'pH increases gradually with each 5.0 mL addition in the buffer region',
              'Record both the volume added and the pH after each increment',
              'The pH change per increment will begin to accelerate as the endpoint approaches',
            ],
          }
        },
        {
          id: 4,
          title: 'Titrate: 1.0 mL Increments',
          shortDescription: 'Switch to 1.0 mL increments until within 2 mL of the end point',
          handler: 'handleTitrateBuffer',
          infoContent: {
            title: 'Approaching Endpoint',
            content: 'Continue with successive additions of **1.0 mL** base until within **2 mL** of the end point. Record volume and pH after each addition.',
            whatHappens: 'Smaller 1.0 mL increments capture the faster pH changes as the buffer capacity diminishes and the equivalence point approaches.',
            observe: [
              'pH begins to change more rapidly per mL of NaOH added',
              'Record volume and pH after each 1.0 mL addition',
              'Switch to 0.5 mL increments when within 2 mL of the expected endpoint',
            ],
          }
        },
        {
          id: 5,
          title: 'Titrate: 0.5 mL Increments',
          shortDescription: 'Switch to 0.5 mL increments of base near the endpoint',
          handler: 'handleTitrateNearEndpoint',
          infoContent: {
            title: 'Near Endpoint',
            content: 'When within 2 mL of the endpoint, add **0.5 mL increments** of base. pH changes more rapidly in this region.',
            whatHappens: 'At 0.5 mL increments, the rapid pH change near the equivalence point is captured with finer resolution to accurately locate the inflection point.',
            observe: [
              'Each 0.5 mL addition now causes a large pH change (possibly 1-2 pH units)',
              'The titration curve is becoming nearly vertical in this region',
              'Switch to 0.1 mL increments at the steepest part of the curve',
            ],
          }
        },
        {
          id: 6,
          title: 'Titrate: 0.1 mL Increments at Endpoint',
          shortDescription: 'As the endpoint is approached, add 0.1 mL increments. Continue until pH no longer changes (to pH 12).',
          handler: 'handleTitrateAtEndpoint',
          infoContent: {
            title: 'Endpoint Region — 0.1 mL Increments',
            content: 'As the endpoint is approached, add **0.1 mL increments**. When the pH no longer changes upon the addition of base, the titration is complete (to **pH 12**). Determine the end point from the inflection point of the curve.',
            whatHappens: 'The finest 0.1 mL increments precisely capture the equivalence point where the maximum rate of pH change occurs (inflection point of the S-curve).',
            observe: [
              'Very small 0.1 mL additions cause the largest pH jumps at the equivalence point',
              'Continue until pH reaches ~12 and no longer changes with additional base',
              'The inflection point (steepest slope) marks the equivalence point volume',
            ],
          }
        }
      ]
    },
    {
      id: 4,
      name: 'analysis',
      title: 'Part IV: Data Analysis',
      equipment: [
        'Calculator or computer',
        'Graph paper or plotting software'
      ],
      initialState: {
        curve: {
          plotted: false,
          derivativePlotted: false
        },
        results: {
          equivalenceVolume: null,
          molesAspirin: null,
          massAspirin: null,
          purityPercent: null
        },
        firstDerivative: []
      },
      steps: [
        {
          id: 1,
          title: 'Plot Titration Curve',
          shortDescription: 'Plot pH vs. mL 0.1 M NaOH added. Label axes: x = mL NaOH added, y = pH.',
          handler: 'handlePlotCurve',
          infoContent: {
            title: 'Titration Curve',
            content: 'Plot the pH titration curve on graph paper or with Excel, labelling the axes: abscissa (horizontal, x axis) = "mL 0.1 M NaOH added"; ordinate (vertical, y axis) = "pH". Plot the points and draw a smooth line through them.',
            whatHappens: 'The pH data is plotted against volume of NaOH added, producing the characteristic S-shaped (sigmoidal) weak acid-strong base titration curve.',
            observe: [
              'The curve shows a gradual rise (buffer region), a steep jump (equivalence point), then a plateau',
              'Label axes: x = mL 0.1 M NaOH added, y = pH',
              'Draw a smooth curve through the data points — do not connect with straight lines',
            ],
          }
        },
        {
          id: 2,
          title: 'Calculate First Derivative',
          shortDescription: 'Calculate ΔpH/ΔV for each interval and plot at midpoint volume',
          handler: 'handleCalculateDerivative',
          infoContent: {
            title: 'First Derivative Method',
            content: `For each pair of consecutive data points, calculate:

**ΔpH/ΔV = (pH₂ − pH₁) / (V₂ − V₁)**

Plot each derivative value at the **midpoint volume**: V_avg = (V₁ + V₂) / 2

The **equivalence point** is the volume where ΔpH/ΔV reaches its **maximum** value — the steepest part of the S-curve.`,
            whatHappens: 'The first derivative (dpH/dV) is calculated for each interval and plotted at the midpoint volume; the peak of the derivative curve pinpoints the equivalence point.',
            observe: [
              'Calculate dpH/dV = (pH2 - pH1) / (V2 - V1) for each consecutive pair',
              'Plot each value at the midpoint volume: V_avg = (V1 + V2) / 2',
              'The maximum of the first derivative curve corresponds to the equivalence point',
            ],
          }
        },
        {
          id: 3,
          title: 'Plot First Derivative',
          shortDescription: 'Plot dpH/dV vs. Volume to visualize the endpoint peak',
          handler: 'handlePlotDerivative'
        },
        {
          id: 4,
          title: 'Find Equivalence Point',
          shortDescription: 'Determine equivalence point by finding the inflection point. Draw perpendicular to abscissa.',
          handler: 'handleFindEquivalencePoint',
          infoContent: {
            title: 'Inflection Point Method',
            content: 'Determine the equivalence point by finding the **inflection point** of the curve (see professor for calculation details). Draw a **perpendicular line** from this point to the abscissa. The point of inflection represents the volume of NaOH required to neutralize the CH₃CO₂C₆H₄COOH in the aspirin tablets.',
            whatHappens: 'The inflection point (maximum of the first derivative) is identified as the equivalence point, giving the precise volume of NaOH required to neutralize all the aspirin.',
            observe: [
              'Identify the volume at which dpH/dV reaches its maximum value',
              'Draw a perpendicular line from this point down to the volume axis',
              'This volume represents the moles of NaOH equal to moles of ASA (1:1 stoichiometry)',
            ],
          }
        },
        {
          id: 5,
          title: 'Calculate Moles of Aspirin',
          shortDescription: 'Calculate moles of aspirin from moles of NaOH at equivalence. Moles ASA = M(NaOH) × V(NaOH).',
          handler: 'handleCalculateMoles',
          infoContent: {
            title: 'Stoichiometry',
            content: 'Based on the data obtained, calculate the percentage of ASA in a typical aspirin tablet. Aspirin (CH₃CO₂C₆H₄COOH) reacts with NaOH. Moles aspirin = moles NaOH = M × V.',
            chemicalEquation: 'CH₃CO₂C₆H₄COOH + NaOH → CH₃CO₂C₆H₄COONa + H₂O',
            whatHappens: 'The moles of NaOH at the equivalence point equal the moles of aspirin (1:1 stoichiometry), calculated as moles = molarity x volume (in litres).',
            observe: [
              'Moles ASA = moles NaOH = M(NaOH) x V(NaOH) at equivalence',
              'Use the standardized NaOH molarity from Part II, not the nominal 0.1 M',
              'Remember the solution was 50 mL out of 100 mL total — account for this',
            ],
          }
        },
        {
          id: 6,
          title: 'Calculate Mass of Aspirin',
          shortDescription: 'Convert moles to mass using MW = 180.16 g/mol',
          handler: 'handleCalculateMass',
          infoContent: {
            title: 'Mass Calculation',
            content: `mass aspirin = moles × MW = moles × **180.16 g/mol**

Example: If endpoint = 18.05 mL of 0.100 M NaOH:
- moles = 0.01805 L × 0.100 mol/L = 0.001805 mol
- mass = 0.001805 × 180.16 = **0.3252 g** (325.2 mg)`,
            whatHappens: 'The moles of aspirin are converted to mass using the molecular weight of acetylsalicylic acid (180.16 g/mol).',
            observe: [
              'mass ASA = moles x 180.16 g/mol',
              'This gives the mass of ASA in the 50 mL aliquot (half the total solution)',
              'Double this value to get the total ASA mass from 4 tablets',
            ],
          }
        },
        {
          id: 7,
          title: 'Calculate Percentage of ASA',
          shortDescription: 'Calculate the percentage of ASA in a typical aspirin tablet',
          handler: 'handleCalculatePurity',
          infoContent: {
            title: 'ASA Percentage',
            content: 'Based on the data obtained, calculate the **percentage of ASA** (acetylsalicylic acid) in a typical aspirin tablet. Use the average tablet mass from the 4 tablets weighed. The ionization constant Ka can also be ascertained from the titration curve.',
            whatHappens: 'The mass of ASA per tablet is divided by the average tablet mass to determine the percentage purity of ASA in the aspirin tablets.',
            observe: [
              '% ASA = (mass of ASA per tablet / average tablet mass) x 100',
              'Divide the total ASA mass by 4 to get the per-tablet value',
              'The half-equivalence point pH on the titration curve equals the pKa of aspirin',
            ],
          }
        }
      ]
    }
  ],
  quiz: {
    experimentId: 'exp11',
    title: 'Aspirin Determination Quiz',
    questions: [
      {
        id: 'exp11-q1',
        type: 'mcq' as const,
        question: 'What is the purpose of calibrating the pH meter with pH 7 and pH 4 buffers?',
        options: [
          { id: 'a', text: 'To clean the electrode' },
          { id: 'b', text: 'To establish a two-point calibration ensuring accurate readings across the pH range' },
          { id: 'c', text: 'To test if the meter is working' },
          { id: 'd', text: 'To neutralize the electrode' },
        ],
        correctOptionId: 'b',
        explanation: 'Two-point calibration (pH 7 for standardization, pH 4 for slope) ensures the pH meter gives accurate readings across the acidic range used in the aspirin titration.',
      },
      {
        id: 'exp11-q2',
        type: 'mcq' as const,
        question: 'How is the equivalence point determined in a potentiometric titration?',
        options: [
          { id: 'a', text: 'When the solution changes color' },
          { id: 'b', text: 'At the inflection point of the S-shaped pH vs. volume curve' },
          { id: 'c', text: 'When pH reaches 7.0' },
          { id: 'd', text: 'When bubbles stop forming' },
        ],
        correctOptionId: 'b',
        explanation: 'The equivalence point is at the inflection point (steepest part) of the S-shaped titration curve, where ΔpH/ΔV is maximum.',
      },
      {
        id: 'exp11-q3',
        type: 'calculation' as const,
        question: 'If 4 aspirin tablets weigh 2.5432 g total and the labeled dose is 325 mg ASA per tablet, what is the expected total ASA mass?',
        expectedValue: 1300,
        tolerance: 2,
        unit: 'mg',
        explanation: 'Total expected ASA = 4 tablets × 325 mg/tablet = 1300 mg',
      },
      {
        id: 'exp11-q4',
        type: 'short_answer' as const,
        question: 'Why are smaller titrant increments (0.5 mL, then 0.1 mL) used near the equivalence point?',
        sampleAnswer: 'Near the equivalence point, pH changes rapidly with small additions of NaOH. Using smaller increments provides more data points in this critical region, allowing more precise determination of the inflection point and thus a more accurate equivalence point volume.',
      },
      {
        id: 'exp11-q5',
        type: 'mcq' as const,
        question: 'What is the stoichiometric ratio of aspirin (ASA) to NaOH in this titration?',
        options: [
          { id: 'a', text: '1:2' },
          { id: 'b', text: '2:1' },
          { id: 'c', text: '1:1' },
          { id: 'd', text: '1:3' },
        ],
        correctOptionId: 'c',
        explanation: 'Aspirin (acetylsalicylic acid) is a monoprotic acid that reacts with NaOH in a 1:1 ratio: ASA + NaOH → NaASA + H₂O.',
      },
    ],
  },
};

export default experiment11Config;
